import { z } from "zod"

// Time series data point (existing, for standard charts)
export const TimeSeriesDataPointSchema = z.object({
  date: z.string(),
  count: z.number(),
  projected: z.boolean().optional(),
  projected_count: z.number().optional(),
})

// Breakdown data point (for pie charts, horizontal bars)
export const BreakdownDataPointSchema = z.object({
  label: z.string(),
  value: z.number(),
  color: z.string().optional(),
})

// Series data point (for multi-series charts)
export const SeriesDataPointSchema = z.object({
  date: z.string(),
  value: z.number(),
})

// Series (for multi-series charts)
export const SeriesSchema = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string().optional(),
  data: z.array(SeriesDataPointSchema),
})

// Time series chart
export const TimeSeriesChartSchema = z.object({
  id: z.string(),
  label: z.string(),
  chart_type: z.literal("time_series"),
  event: z.string().optional(),
  interval: z.enum(["day", "month"]).optional(),
  unique: z.boolean().optional(),
  projection: z.boolean().optional(),
  color: z.string().nullish(),
  data: z.array(TimeSeriesDataPointSchema),
})

// Breakdown chart (pie chart)
export const BreakdownChartSchema = z.object({
  id: z.string(),
  label: z.string(),
  chart_type: z.literal("breakdown"),
  color: z.string().nullish(),
  data: z.array(BreakdownDataPointSchema),
})

// Multi-series chart (stacked area, multi-line)
export const MultiSeriesChartSchema = z.object({
  id: z.string(),
  label: z.string(),
  chart_type: z.literal("multi_series"),
  value_format: z.enum(["percentage", "number"]).nullish(),
  color: z.string().nullish(),
  series: z.array(SeriesSchema),
})

// Discriminated union of all chart types
export const ChartSchema = z.discriminatedUnion("chart_type", [
  TimeSeriesChartSchema,
  BreakdownChartSchema,
  MultiSeriesChartSchema,
])

// Type exports
export type TimeSeriesDataPoint = z.infer<typeof TimeSeriesDataPointSchema>
export type BreakdownDataPoint = z.infer<typeof BreakdownDataPointSchema>
export type SeriesDataPoint = z.infer<typeof SeriesDataPointSchema>
export type Series = z.infer<typeof SeriesSchema>
export type TimeSeriesChart = z.infer<typeof TimeSeriesChartSchema>
export type BreakdownChart = z.infer<typeof BreakdownChartSchema>
export type MultiSeriesChart = z.infer<typeof MultiSeriesChartSchema>
export type Chart = z.infer<typeof ChartSchema>

// Legacy exports for backwards compatibility
export const ChartDataPointSchema = TimeSeriesDataPointSchema
export type ChartDataPoint = TimeSeriesDataPoint
